import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import Underline from '@editorjs/underline';

import { BlockMenu, BlockReferencedIndicator } from '@/components/atoms';
import {
  ReferenceTool,
  ParagraphTool,
  NoteTool,
  ExampleTool,
  ExceptionTool,
} from '@/editorjs-tools';
import GlobalMoveTune from '@/editorjs-tools/move-tune';
import { ColorTool } from '@/editorjs-tools/color';
import { FontSizeTool } from '@/editorjs-tools/font-size';
import { useNamesMap } from '@/context/names/names-context';
import { useReferenceMetas } from '@/context/references/reference-meta-store-context';
import { useReferenceRowLookup } from '@/context/references/reference-row-index-context';
import { useIncomingReferencesMap } from '@/context/incoming-references/incoming-references-context';
import {
  applyEditorJsOutput,
  canonicalizeStoredText,
  translateBlockIds,
} from '@/lib/editorjs-save-adapter';
import {
  extractRefTokenIds,
  renderTextToHtml,
  resolveRefMeta,
} from '@/lib/edit-text-serialize';
import { sanitizeBlockHtml } from '@/lib/sanitize';
import { styleRuleToLeafCssVars, useStyleRule } from '@/lib/style-profile';
import {
  editorToolForStyle,
  styleForEditorTool,
  buildSourceScopedRefKey,
} from '@worldathletics/utilities';
import {
  useOpenEditLink,
  useEditLinkActiveSourceId,
} from '@/context/edit-link/edit-link-context';
import { resolveEditLinkOpenFromRange } from '@/lib/resolve-edit-link-open';
import { toast } from 'sonner';
import type { RootState } from '@/context/store/store';
import { moveNode } from '@/context/store/slices/documentDataSlice';
import {
  setAddBlockIntent,
  setNewBlockModal,
  setDeleteBlockConfirmationModal,
} from '@/context/store/slices/siteSlice';
import { getSiblingPosition } from '@/hooks/use-sibling-position';
import { registerEditorFlush, setEditorFlushDirty } from '@/lib/editor-flush-registry';
import { useViewportEditorActivation } from '@/hooks/use-viewport-editor-activation';
import { useEditorInteractionPins } from '@/hooks/use-editor-interaction-pins';

import {
  findPreviousBlockElement,
  isCaretAtStartOfElement,
  isStructuralEditorChange,
  resolveSyncedDocId,
} from './editor-block-id';
import './document-editor-paragraph-cluster.scss';

type Sibling = {
  id: string;
  type: 'text';
  style: 'body' | 'note' | 'example' | 'exception';
  order: number;
  text: string;
};

// Maps the persistent leaf-text style to the EditorJS tool name registered in
// the cluster's `tools` config. Clusters are split by style so siblings share
// one tool type per EditorJS instance.
const toolForStyle = editorToolForStyle;

type Props = {
  parentId: string;
  siblings: Sibling[];
  depth?: number;
};

const DEBOUNCE_MS = 300;

function DocumentEditorParagraphCluster({
  parentId,
  siblings,
  depth = 0,
}: Props) {
  const bookId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[0],
  );
  const nodeId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[1],
  );
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const openEditLink = useOpenEditLink();

  // Only subscribe to the reference rows this cluster's own siblings
  // actually cite — a reference edit elsewhere in the document (a
  // different cluster's tokens) never re-renders this one. Subscribes to
  // both the plain token id and its source-scoped form (see
  // `buildSourceScopedRefKey`) since `buildReferenceMetaMap` now registers
  // legacy internal tokens under both — a row-only edit that only touches
  // the scoped entry would otherwise go unnoticed. `tokenIdsRef` keeps the
  // array referentially stable across renders whose extracted ids are
  // unchanged, so `useReferenceMetas` doesn't resubscribe on every
  // keystroke-driven re-render.
  const rawTokenIds = useMemo(
    () => [
      ...new Set(
        siblings.flatMap(s =>
          extractRefTokenIds(s.text).flatMap(id => [
            id,
            buildSourceScopedRefKey(s.id, id),
          ]),
        ),
      ),
    ],
    [siblings],
  );
  const tokenIdsRef = useRef<string[]>(rawTokenIds);
  if (
    tokenIdsRef.current.length !== rawTokenIds.length ||
    !tokenIdsRef.current.every((id, i) => id === rawTokenIds[i])
  ) {
    tokenIdsRef.current = rawTokenIds;
  }
  const refMap = useReferenceMetas(tokenIdsRef.current);
  const namesMap = useNamesMap();
  const { getReferences } = useReferenceRowLookup();
  const incomingRefsMap = useIncomingReferencesMap();
  const incomingRefsMapRef = useRef(incomingRefsMap);
  useEffect(() => {
    incomingRefsMapRef.current = incomingRefsMap;
  }, [incomingRefsMap]);
  const siblingsRef = useRef(siblings);

  useEffect(() => {
    siblingsRef.current = siblings;
  }, [siblings]);

  // Homogeneous style per cluster after splitChildrenIntoClusters groups by
  // style. Use the first sibling's style for CSS vars and block chrome.
  const firstStyle = siblings[0]?.style ?? 'body';
  const rule = useStyleRule(firstStyle, depth);

  const styleVars = styleRuleToLeafCssVars(rule);

  // Latest namesMap accessible from non-React callbacks (EditorJS onReady /
  // onChange) without re-creating the editor instance. Synced via effect so
  // we don't write to refs during render.
  const namesMapRef = useRef(namesMap);
  useEffect(() => {
    namesMapRef.current = namesMap;
  });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const holderRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EditorJS | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parentIdRef = useRef(parentId);

  // Viewport-gated activation (Phase 5): keeps far-offscreen clusters from
  // each carrying a live EditorJS instance on very large documents. `pin`/
  // `unpin` are stable across renders (see `useViewportEditorActivation`),
  // so effects below can safely depend on them without re-subscribing.
  const { shouldMountEditor, pin, unpin } =
    useViewportEditorActivation(wrapperRef);
  useEditorInteractionPins(wrapperRef, { pin, unpin });
  const shouldMountEditorRef = useRef(shouldMountEditor);
  useEffect(() => {
    shouldMountEditorRef.current = shouldMountEditor;
  });

  // Pin while one of this cluster's siblings is the active EditLink source
  // — the modal may stay open long enough for the user to scroll this
  // cluster out of the observed viewport bounds while picking a target.
  const activeEditLinkSourceId = useEditLinkActiveSourceId();
  useEffect(() => {
    const isActiveSource =
      activeEditLinkSourceId != null &&
      siblings.some(s => s.id === activeEditLinkSourceId);
    if (isActiveSource) {
      pin('modal');
    } else {
      unpin('modal');
    }
  }, [activeEditLinkSourceId, siblings, pin, unpin]);

  // `null` → EditorJS is (or should be) mounted. Non-null → deactivated:
  // holds the height measured just before destroy so the static
  // equivalent doesn't collapse the layout while offscreen.
  const [staticSnapshot, setStaticSnapshot] = useState<{
    minHeight: number;
  } | null>(() => (shouldMountEditor ? null : { minHeight: 0 }));
  // Bumped on every reactivation (static → editor) so the EditorJS
  // creation effect re-runs and constructs a fresh instance from
  // `siblingsRef.current` — which is synced unconditionally on every
  // `siblings` prop change AND advanced by `runSave` after our own edits,
  // so it always reflects the latest text, never a stale mount snapshot.
  const [mountEpoch, setMountEpoch] = useState(0);
  const deactivationInFlightRef = useRef(false);
  // Reserves the last on-screen height across the static → editor swap on
  // reactivation. Construction is deferred (see the `setTimeout` in the
  // creation effect below), so without this the holder briefly renders
  // empty at `min-height: 1.5em` — collapsing a several-hundred-pixel block
  // for a frame and shifting everything below it, which is exactly what
  // moves the scroll position onto the next sibling when this happens above
  // the viewport. Cleared once EditorJS reports `onReady`.
  const [reservedHeight, setReservedHeight] = useState<number | null>(null);
  // Persistent translation: EditorJS auto-generates short block ids (e.g.
  // 'xY7zKq'); we mint stable UUIDs for them on first sight and reuse the
  // mapping on every subsequent save so the same block keeps its DocumentData
  // id across keystrokes.
  const idMappingRef = useRef<Map<string, string>>(new Map());
  // Doc ids this EditorJS instance has legitimately held since it was last
  // constructed or fully re-synced — grows on construction/reconciliation/
  // successful save, never shrinks. Used by `runSave`'s resurrection guard
  // to tell "existed before, now gone everywhere else" (drop it) apart from
  // "genuinely new, about to be added" (keep it) WITHOUT reading current
  // Redux state for that distinction — Redux may have already dropped the
  // id by the time a stale save reads it, which is exactly the race this
  // guards against.
  const knownEditorIdsRef = useRef<Set<string>>(new Set());
  const isReadyRef = useRef(false);
  const isDestroyedRef = useRef(false);
  const isProgrammaticRenderRef = useRef(false);

  // Track the actual block elements so we can portal menus into them. Keeps
  // the raw EditorJS block id alongside the resolved doc id so menu actions
  // can re-resolve it at click time (see flushPendingSave below).
  const [blockElements, setBlockElements] = useState<
    { id: string; editorBlockId: string; el: HTMLElement }[]
  >([]);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Pin while our own block-level menu (BlockMenu, `openMenuId`) is open —
  // EditorJS's built-in inline toolbar is covered by the `'selection'` pin
  // wired in `useEditorInteractionPins`.
  useEffect(() => {
    if (openMenuId) {
      pin('menu');
    } else {
      unpin('menu');
    }
  }, [openMenuId, pin, unpin]);

  // Reconciliation: Track the last known state of the editor blocks
  const lastSyncedSiblingsRef = useRef(siblings);
  // Set when the reconciliation effect finds Redux siblings changed while a
  // save was in flight — re-run the sync once that save's debounce clears,
  // instead of silently dropping the update.
  const pendingReconcileRef = useRef(false);

  useEffect(() => {
    parentIdRef.current = parentId;
  });

  const syncBlockNames = (): void => {
    const root = holderRef.current;
    if (!root) return;
    const map = namesMapRef.current;
    const blocks = root.querySelectorAll<HTMLElement>('.ce-block');
    const newBlockEls: {
      id: string;
      editorBlockId: string;
      el: HTMLElement;
    }[] = [];

    blocks.forEach(blockEl => {
      const editorBlockId = blockEl.getAttribute('data-id') ?? '';
      const docId = idMappingRef.current.get(editorBlockId) ?? editorBlockId;
      blockEl.setAttribute('data-doc-id', docId);
      const name = map.get(docId);
      if (name) blockEl.setAttribute('data-name', name);
      else blockEl.removeAttribute('data-name');

      // Find the content element where we can anchor our menu
      const contentEl =
        blockEl.querySelector<HTMLElement>('.ce-block__content');
      if (contentEl) {
        newBlockEls.push({ id: docId, editorBlockId, el: contentEl });
      }
    });

    setBlockElements(newBlockEls);
  };

  // `editor.blocks.render()` internally clears every block before
  // re-inserting them (BlockManager.clear() → Renderer.render()) — for the
  // duration of that await, the editor genuinely holds zero blocks. Queueing
  // renders one-at-a-time (rather than firing them independently) and
  // tracking how many are in flight lets `runSave` (below) wait out that
  // window instead of ever reading the editor mid-clear.
  const renderQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingRenderCountRef = useRef(0);

  // Renders `targetSiblings` into this cluster's EditorJS instance, replacing
  // whatever it currently shows. Shared by the reconciliation effect below
  // and by the onChange handler once a blocked reconciliation is retried.
  function syncEditorToSiblings(targetSiblings: Sibling[]): void {
    const editor = editorRef.current;
    if (!editor) return;

    const newBlocks = targetSiblings.map(s => ({
      id: s.id,
      type: toolForStyle(s.style),
      data: { text: renderTextToHtml(s.text, refMap, s.id) },
    }));

    // Advance the reconciliation baseline synchronously — a dependency
    // change that arrives while this render is still queued/in-flight (e.g.
    // a references refetch) then reads as "already in sync" instead of
    // scheduling a second, overlapping `blocks.render()` on top of this one.
    lastSyncedSiblingsRef.current = targetSiblings;
    for (const s of targetSiblings) knownEditorIdsRef.current.add(s.id);
    isProgrammaticRenderRef.current = true;
    pendingRenderCountRef.current += 1;

    renderQueueRef.current = renderQueueRef.current
      .then(() => editor.isReady)
      .then(() => editor.blocks.render({ blocks: newBlocks }))
      .then(() => {
        syncBlockNames();
      })
      .catch(() => {
        // The holder/instance may have been torn down mid-render (viewport
        // deactivation racing this call) — nothing left to reconcile here.
      })
      .then(() => {
        pendingRenderCountRef.current -= 1;
        if (pendingRenderCountRef.current > 0) return;
        // EditorJS's own mutation-driven onChange can fire a microtask after
        // this promise resolves (e.g. sanitizer touching the rendered DOM).
        // Deferring the flag reset past the current microtask queue keeps
        // that spurious change from being mistaken for a real user edit.
        setTimeout(() => {
          if (pendingRenderCountRef.current === 0) {
            isProgrammaticRenderRef.current = false;
          }
        }, 0);
      });
  }
  const syncEditorToSiblingsRef = useRef(syncEditorToSiblings);
  useEffect(() => {
    syncEditorToSiblingsRef.current = syncEditorToSiblings;
  });

  // Saves the editor's current content to Redux: editor.save() → translate
  // short EditorJS ids to stable doc UUIDs → diff against the last known
  // siblings → dispatch add/update/remove/reorder. Shared by the debounced
  // onChange handler and by flushPendingSave (called synchronously before a
  // block-menu action so it can never operate on a stale/unsynced id).
  async function runSave(): Promise<void> {
    // A programmatic render (see `syncEditorToSiblings`) briefly clears the
    // editor's blocks mid-`blocks.render()`. Reading the editor in that
    // window would misread "temporarily empty" as "the user deleted
    // everything" — wait for any in-flight/queued renders to fully settle
    // before touching the editor at all.
    while (pendingRenderCountRef.current > 0) {
      await renderQueueRef.current;
    }

    const e = editorRef.current;
    if (
      isDestroyedRef.current ||
      !isReadyRef.current ||
      !e ||
      typeof e.save !== 'function'
    ) {
      debounceRef.current = null;
      return;
    }
    const output = await e.save();
    const state = store.getState();
    const docState = state.documentData;
    const knownIds = new Set(docState.byId ? Object.keys(docState.byId) : []);

    // The saver silently omits blocks whose tool reports validate() ===
    // false (e.g. blank text without preserveBlank). Compare against the
    // editor's actual live block ids — never `output.blocks` alone — so a
    // skipped-but-still-present block is never mistaken for a deletion.
    const liveEditorIds = new Set<string>();
    const blocksCount = e.blocks.getBlocksCount();
    for (let i = 0; i < blocksCount; i += 1) {
      const liveId = e.blocks.getBlockByIndex(i)?.id;
      if (liveId) liveEditorIds.add(liveId);
    }

    // Belt-and-braces against the same mid-clear window (or any other path
    // that leaves the editor transiently empty): there is no legitimate case
    // where a live cluster's editor reports zero blocks while Redux still
    // has siblings for it. Bail out untouched — in particular, BEFORE
    // `translateBlockIds` runs, since it would otherwise prune every entry
    // out of `idMappingRef` against this empty live set.
    if (
      (liveEditorIds.size === 0 || output.blocks.length === 0) &&
      siblingsRef.current.length > 0
    ) {
      debounceRef.current = null;
      return;
    }

    // `translateBlockIds` rewrites EVERY block's `.id` to its resolved doc
    // UUID (pre-existing, previously-mapped, or freshly minted) — the
    // original short EditorJS id never survives into its output. Snapshot
    // which doc ids `idMappingRef` already resolved to BEFORE this call so
    // a doc id minted fresh just now (added to the map by this very call)
    // can still be told apart from one that already existed.
    const previouslyMintedDocIds = new Set(idMappingRef.current.values());

    const safe = translateBlockIds(
      output,
      knownIds,
      idMappingRef.current,
      liveEditorIds,
    );
    const liveEditorBlockIds = new Set(
      [...liveEditorIds].map(
        editorId => idMappingRef.current.get(editorId) ?? editorId,
      ),
    );

    // A doc id this editor instance already held (per `knownEditorIdsRef` —
    // deliberately NOT current Redux state, which may have already dropped
    // it because of the very external mutation this guards against) but
    // that's now absent from both the current siblings and Redux itself was
    // removed by something else (delete-block flow, backspace-merge) while
    // this editor had not yet re-rendered to reflect it. Drop it from this
    // save entirely instead of reviving it via `addNode` below. A doc id
    // that was NEVER previously known here is genuinely new — about to be
    // added for the first time — and must never be filtered even though it
    // isn't anywhere yet either.
    const byIdAfterSave = store.getState().documentData.byId;
    const siblingIdSet = new Set(siblingsRef.current.map(s => s.id));
    safe.blocks = safe.blocks.filter(block => {
      const docId = block.id ?? '';
      const wasPreviouslyKnown =
        knownEditorIdsRef.current.has(docId) ||
        previouslyMintedDocIds.has(docId);
      if (!wasPreviouslyKnown) return true;
      if (siblingIdSet.has(docId) || byIdAfterSave[docId]) return true;
      liveEditorBlockIds.delete(docId);
      return false;
    });
    for (const block of safe.blocks) {
      knownEditorIdsRef.current.add(block.id ?? '');
    }

    // Diff only this cluster's blocks — editor.save() returns blocks owned
    // by this EditorJS instance, not every text leaf under the parent
    // container (other clusters are separate editors).
    const prev = siblingsRef.current.map(s => ({
      id: s.id,
      text: s.text,
      style: s.style,
    }));
    const prevSiblingById = new Map(siblingsRef.current.map(s => [s.id, s]));

    const effectiveOrder = applyEditorJsOutput({
      parentId: parentIdRef.current,
      prev,
      output: safe,
      getParentChildren: () =>
        store.getState().documentData.childrenById?.[parentIdRef.current] ?? [],
      dispatch: dispatch as Parameters<
        typeof applyEditorJsOutput
      >[0]['dispatch'],
      liveEditorBlockIds,
    });

    // Update the sync ref so the reconciliation effect doesn't trigger a
    // redundant render for our own changes. Build from `effectiveOrder`
    // (not `safe.blocks` alone) so survivor blocks — live in the editor but
    // skipped by the saver — stay represented with their unchanged prior
    // row instead of silently disappearing from our view of this cluster.
    const savedById = new Map(
      safe.blocks.map(b => {
        const docId = idMappingRef.current.get(b.id!) ?? b.id!;
        const style = styleForEditorTool(b.type ?? 'paragraph');
        return [
          docId,
          {
            id: docId,
            text: canonicalizeStoredText(b.data.text as string),
            style,
            type: 'text' as const,
            order: 0,
          },
        ] as const;
      }),
    );
    const syncedSiblings = effectiveOrder.map(
      id => savedById.get(id) ?? prevSiblingById.get(id)!,
    );
    lastSyncedSiblingsRef.current = syncedSiblings;
    siblingsRef.current = syncedSiblings;

    // Redux update fires the names useEffect, but for freshly-minted ids we
    // want the prefix/menu id to be correct as quickly as possible — sync
    // directly here too.
    syncBlockNames();

    // This save is done — the reconciliation guard above should only reflect
    // a currently in-flight save, not "a save happened once."
    debounceRef.current = null;
    if (pendingReconcileRef.current) {
      pendingReconcileRef.current = false;
      syncEditorToSiblingsRef.current(siblingsRef.current);
    }
  }
  const runSaveRef = useRef(runSave);
  useEffect(() => {
    runSaveRef.current = runSave;
  });

  // Flushes any pending/in-flight save immediately. Called only from
  // block-menu action handlers (delete / add above / add below) — never
  // from onChange — so a menu action always operates on a block that has a
  // real doc UUID, without adding a dispatch on every keystroke.
  // Serializes overlapping saves: if a save is already in flight, queue a
  // rerun so short EditorJS ids resolve before menu actions (01382910).
  const saveInFlightRef = useRef<Promise<void> | null>(null);
  const saveRequestedRef = useRef(false);

  async function requestSave(): Promise<void> {
    if (saveInFlightRef.current) {
      saveRequestedRef.current = true;
      await saveInFlightRef.current;
      return;
    }

    saveInFlightRef.current = (async () => {
      do {
        saveRequestedRef.current = false;
        await runSaveRef.current();
      } while (saveRequestedRef.current);
    })();

    try {
      await saveInFlightRef.current;
    } finally {
      saveInFlightRef.current = null;
    }
  }

  async function flushPendingSave(): Promise<void> {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    await requestSave();
    setEditorFlushDirty(parentId, false);
  }
  const flushPendingSaveRef = useRef(flushPendingSave);
  useEffect(() => {
    flushPendingSaveRef.current = flushPendingSave;
  });
  const requestSaveRef = useRef(requestSave);
  useEffect(() => {
    requestSaveRef.current = requestSave;
  });

  useEffect(() => {
    let busy = false;
    return registerEditorFlush(parentId, {
      flush: async () => {
        busy = true;
        try {
          await flushPendingSaveRef.current();
        } finally {
          busy = false;
          setEditorFlushDirty(parentId, false);
        }
      },
      cancel: () => {
        if (debounceRef.current !== null) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        setEditorFlushDirty(parentId, false);
      },
      isBusy: () => busy || debounceRef.current !== null,
    });
  }, [parentId]);

  // Reconciliation: Detect external changes (like "Move Up/Down" from a different menu or "Add Block")
  // and force EditorJS to re-render its block list.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Check if the siblings structure changed significantly from Redux side
    const currentIds = siblings.map(s => s.id);
    const lastIds = lastSyncedSiblingsRef.current.map(s => s.id);

    const isChanged =
      currentIds.length !== lastIds.length ||
      !currentIds.every((id, i) => id === lastIds[i]);

    if (isChanged) {
      // Do not overwrite in-flight EditorJS edits while a debounced save is
      // pending — retry once that save clears (see onChange handler below)
      // instead of dropping this update on the floor.
      if (debounceRef.current !== null) {
        pendingReconcileRef.current = true;
        return;
      }

      syncEditorToSiblings(siblings);
    }
    // Deliberately NOT depending on `refMap`: this effect only diffs block
    // ids, which a references refetch never changes. Chip labels are
    // repainted in place by the dedicated effect below — including `refMap`
    // here used to fire a second, overlapping `blocks.render()` on every
    // references-query refetch (e.g. right after a block delete, which also
    // invalidates that query), racing the render already triggered by the
    // sibling-id change itself.
  }, [siblings]);

  const handleGlobalMove = (
    editorBlockId: string,
    direction: 'up' | 'down',
  ) => {
    const docId = idMappingRef.current.get(editorBlockId) ?? editorBlockId;
    const currentState = store.getState();
    const parentChildren =
      currentState.documentData.childrenById[parentIdRef.current] ?? [];
    const currentIndex = parentChildren.indexOf(docId);

    if (currentIndex === -1) return;

    if (direction === 'up') {
      if (currentIndex === 0) return;
      dispatch(
        moveNode({
          id: docId,
          newParentId: parentIdRef.current,
          newIndex: currentIndex - 1,
        }),
      );
    } else {
      if (currentIndex === parentChildren.length - 1) return;
      dispatch(
        moveNode({
          id: docId,
          newParentId: parentIdRef.current,
          newIndex: currentIndex + 1,
        }),
      );
    }
  };

  useEffect(() => {
     
    syncBlockNames();
    // syncBlockNames reads from refs; we want this effect to fire whenever
    // the names map identity changes (most often: structural edit elsewhere
    // re-renders the parent and computes a new map).
  }, [namesMap, siblings]);

  // Reconcile chip data-ref-id AND labels with the source of truth.
  //
  // Two distinct triggers, one effect:
  //   1. refMap updates (references query refetched) → chip labels need
  //      refreshing. Init captures refMap once; if the references query had
  //      not resolved yet, every chip rendered as `data-broken="true"` with
  //      its refId as text.
  //   2. siblings prop updates (the source text changed externally — e.g.
  //      applyOps rewrote `{{ref:OLD}}` → `{{ref:NEW}}` via EditLink Confirm)
  //      → chip data-ref-id is stale because EditorJS owns the DOM after init
  //      and never re-renders the text on its own. Without reconciling, the
  //      chip stays pointed at OLD (which is no longer in refMap after the
  //      old Reference row was deleted) → renders broken until the user
  //      refreshes the page.
  //
  // For each EditorJS block: resolve its data-id → doc UUID via the id
  // mapping; look up the matching sibling; extract `{{ref:UUID}}` tokens
  // from the sibling's text IN ORDER; walk the chip elements in DOM order;
  // pair them up and rewrite data-ref-id + textContent.
  useEffect(() => {
    const root = holderRef.current;
    if (!root) return;
    const siblingByDocId = new Map(siblings.map(s => [s.id, s] as const));
    const blocks = root.querySelectorAll<HTMLElement>('.ce-block');
    blocks.forEach(blockEl => {
      const editorBlockId = blockEl.getAttribute('data-id') ?? '';
      const docId = idMappingRef.current.get(editorBlockId) ?? editorBlockId;
      blockEl.setAttribute('data-doc-id', docId);
      const sibling = siblingByDocId.get(docId);
      const chips = blockEl.querySelectorAll<HTMLElement>(
        '.ref-chip[data-ref-id]',
      );
      if (!sibling) {
        // Fallback for blocks not in `siblings` (transient during edits):
        // refresh labels only, leave data-ref-id alone.
        chips.forEach(chip => {
          const id = chip.getAttribute('data-ref-id') ?? '';
          const meta = resolveRefMeta(refMap, id, docId);
          if (meta) {
            chip.textContent = meta.name;
            chip.removeAttribute('data-broken');
            if (meta.lost) {
              chip.setAttribute('data-lost', 'true');
            } else {
              chip.removeAttribute('data-lost');
            }
          } else {
            chip.setAttribute('data-broken', 'true');
            chip.removeAttribute('data-lost');
            chip.textContent = id;
          }
        });
        return;
      }
      const tokenIds: string[] = [];
      for (const match of sibling.text.matchAll(/\{\{ref:([^}]+)\}\}/g)) {
        tokenIds.push(match[1]);
      }
      chips.forEach((chip, i) => {
        const targetId = tokenIds[i] ?? chip.getAttribute('data-ref-id') ?? '';
        if (chip.getAttribute('data-ref-id') !== targetId) {
          chip.setAttribute('data-ref-id', targetId);
        }
        const meta = resolveRefMeta(refMap, targetId, docId);
        if (meta) {
          chip.textContent = meta.name;
          chip.removeAttribute('data-broken');
          if (meta.lost) {
            chip.setAttribute('data-lost', 'true');
          } else {
            chip.removeAttribute('data-lost');
          }
        } else {
          chip.setAttribute('data-broken', 'true');
          chip.removeAttribute('data-lost');
          chip.textContent = targetId;
        }
      });
    });
  }, [siblings, refMap]);

  useEffect(() => {
    if (!holderRef.current || editorRef.current) return;
    // Viewport-gated activation: skip constructing EditorJS entirely while
    // offscreen and unpinned. Re-runs (via `mountEpoch`) once
    // `shouldMountEditor` flips back to true — see the reactivation effect
    // below.
    if (!shouldMountEditorRef.current) return;

    isDestroyedRef.current = false;
    isReadyRef.current = false;
    isProgrammaticRenderRef.current = false;

    // React StrictMode (dev) double-invokes effects: mount → cleanup →
    // mount. If we construct EditorJS synchronously, the first constructor
    // kicks off an async render pipeline that can land DOM nodes after the
    // first cleanup runs — producing duplicate blocks once the second init
    // also renders. Defer the constructor to the next microtask so the
    // synchronous StrictMode cleanup fully cancels the first init before
    // any EditorJS instance is ever created.
    let cancelled = false;
    let editor: EditorJS | null = null;
    let keydownHolder: HTMLDivElement | null = null;

    // Tools declare `enableLineBreaks` so EditorJS core skips its own
    // block-split-on-Enter logic (verified at runtime in the installed
    // editorjs.mjs bundle) and falls through to the browser's default
    // contenteditable behavior. That default is inconsistent across
    // browsers (Chrome wraps the new line in a <div>, which our sanitizer
    // doesn't allow and would silently drop) — so we intercept Enter here
    // and insert a deterministic <br> instead. This listener is on the
    // holder (an ancestor of EditorJS's own per-block keydown listeners),
    // so it always runs after EditorJS's core has already decided not to
    // split the block.
    const handleKeyDown = (evt: KeyboardEvent): void => {
      if (evt.key !== 'Enter') return;
      evt.preventDefault();
      document.execCommand('insertLineBreak');
    };

    // EditorJS's own backspace handling (BlockEvents.backspace) calls
    // `event.preventDefault()` and kicks off `BlockManager.mergeBlocks`
    // synchronously, before bubbling reaches an ancestor listener — so
    // stopping a merge requires a CAPTURE-phase listener, unlike the Enter
    // interception above (which deliberately runs after EditorJS's own
    // per-block listener, on bubble). A block that's the target of an
    // incoming reference must be deleted through the block menu (which
    // freezes/warns about backlinks) rather than silently absorbed into its
    // previous sibling by Backspace.
    const handleBackspaceCapture = (evt: KeyboardEvent): void => {
      if (evt.key !== 'Backspace') return;
      const target = evt.target;
      if (!(target instanceof Element)) return;
      const contentEl = target.closest<HTMLElement>('.ce-paragraph');
      if (!contentEl) return;
      if (!isCaretAtStartOfElement(contentEl)) return;

      const blockEl = contentEl.closest<HTMLElement>('.ce-block');
      if (!blockEl || !findPreviousBlockElement(blockEl)) return;

      const editorBlockId = blockEl.getAttribute('data-id') ?? '';
      const docId = idMappingRef.current.get(editorBlockId) ?? editorBlockId;
      const hasIncomingRefs = (incomingRefsMapRef.current[docId] ?? 0) > 0;
      // A merge appends this block's content into the previous one and deletes
      // this block. The API's `remove` op deletes its `DocumentData` row, and
      // `Reference.source` cascades on delete (the constraint is not
      // DEFERRABLE), so every reference authored FROM this block is destroyed
      // — while its `{{ref:…}}` tokens survive inside the merged text, leaving
      // dangling chips. The post-save self-heal only recreates legacy
      // target-id tokens, and even then without `customName` /
      // `detectedPhrase`, so imported labels are lost for good. Read the live
      // DOM rather than `siblingsRef` so unflushed edits are accounted for.
      const hasOutgoingRefs = contentEl.querySelector('.ref-chip') !== null;
      if (!hasIncomingRefs && !hasOutgoingRefs) return;

      evt.preventDefault();
      evt.stopPropagation();
      toast.error(
        hasIncomingRefs
          ? 'This block is referenced elsewhere — delete it from the block menu instead of merging it with Backspace.'
          : 'This block contains references — merging it into the previous block would break them. Remove the reference chips first, then merge.',
      );
    };

    // EditorJS's redactor click handler inserts a new empty block when the
    // user clicks the padding below the last block. We only want new blocks
    // via the Add-block modal (stable UUIDs). Capture-phase stop keeps
    // in-block clicks, toolbars, and menus working.
    const handleRedactorClickCapture = (evt: MouseEvent): void => {
      const target = evt.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.ce-block')) return;
      if (
        target.closest(
          '.ce-toolbar, .ce-inline-toolbar, .ce-settings, .ce-popover, .ce-block__block-menu',
        )
      ) {
        return;
      }
      if (
        target.classList.contains('codex-editor__redactor') ||
        target.classList.contains('codex-editor') ||
        target === keydownHolder
      ) {
        evt.stopPropagation();
      }
    };

    const initTimer = setTimeout(() => {
      if (cancelled || !holderRef.current) return;
      holderRef.current.replaceChildren();
      keydownHolder = holderRef.current;
      keydownHolder.addEventListener('keydown', handleKeyDown);
      keydownHolder.addEventListener('keydown', handleBackspaceCapture, true);
      keydownHolder.addEventListener('click', handleRedactorClickCapture, true);

      // Read the unconditionally-synced snapshot once. `siblingsRef` always
      // reflects the latest text — kept current by the prop-sync effect
      // above and advanced by `runSave` after our own edits — so a
      // reactivation after an edit made while mounted never re-inits from
      // stale pre-edit text.
      const initSiblings = siblingsRef.current;
      const clusterDefaultTool = toolForStyle(initSiblings[0]?.style ?? 'body');

      const initBlocks = initSiblings.map(s => ({
        id: s.id,
        type: toolForStyle(s.style),
        data: { text: renderTextToHtml(s.text, refMap, s.id) },
      }));
      // Re-base the reconciliation baseline too — otherwise a structural
      // edit (id added/removed/reordered) that landed while this cluster
      // was deactivated leaves `lastSyncedSiblingsRef` behind, and the
      // first post-reactivation reconciliation pass fires a redundant full
      // `editor.blocks.render()` over content that already matches.
      lastSyncedSiblingsRef.current = initSiblings;
      // Reset (not merge) — this is a fresh instance, so its resurrection
      // guard should only know about what it's being constructed with.
      knownEditorIdsRef.current = new Set(initSiblings.map(s => s.id));

      editor = new EditorJS({
        holder: holderRef.current,
        // Collapse the EditorJS "bottom zone" that invites click-to-create.
        // Paired with CSS padding override + the click capture guard above.
        minHeight: 0,
        // Any remaining insert path (paste / toolbox) should match this
        // cluster's style instead of defaulting to body/paragraph.
        defaultBlock: clusterDefaultTool,
        tunes: ['globalMove'],
        tools: {
          globalMove: {
            class: GlobalMoveTune as never,
            config: {
              onMoveUp: (id: string) => handleGlobalMove(id, 'up'),
              onMoveDown: (id: string) => handleGlobalMove(id, 'down'),
            },
          },
          // preserveBlank: an empty text block is real document content, not
          // an incomplete edit. Without it, the base Paragraph.validate()
          // returns false for blank text and editor.save() silently drops
          // the block — which the save adapter then reads as a deletion
          // (removing it from Redux without the user ever confirming).
          paragraph: {
            class: ParagraphTool as never,
            inlineToolbar: [
              'bold',
              'italic',
              'underline',
              'reference',
              'color',
              'fontSize',
            ],
            tunes: ['globalMove'],
            config: { preserveBlank: true },
          },
          note: {
            class: NoteTool as never,
            inlineToolbar: [
              'bold',
              'italic',
              'underline',
              'reference',
              'color',
              'fontSize',
            ],
            tunes: ['globalMove'],
            config: { preserveBlank: true },
          },
          example: {
            class: ExampleTool as never,
            inlineToolbar: [
              'bold',
              'italic',
              'underline',
              'reference',
              'color',
              'fontSize',
            ],
            tunes: ['globalMove'],
            config: { preserveBlank: true },
          },
          exception: {
            class: ExceptionTool as never,
            inlineToolbar: [
              'bold',
              'italic',
              'underline',
              'reference',
              'color',
              'fontSize',
            ],
            tunes: ['globalMove'],
            config: { preserveBlank: true },
          },
          underline: {
            class: Underline as never,
          },
          reference: {
            class: ReferenceTool as never,
            config: {
              onPickerOpen: (range: Range) => {
                void flushPendingSaveRef.current().then(() => {
                  const docNodeState = store.getState().documentNode.data;
                  const result = resolveEditLinkOpenFromRange(
                    range,
                    idMappingRef.current,
                    {
                      // Read lazily at picker-open time — no per-cluster
                      // TanStack Query observer, and the closure stays
                      // stable across references-query refetches.
                      refs: getReferences(),
                      getSourceText: sourceBlockId => {
                        const sibling = siblingsRef.current.find(
                          s => s.id === sourceBlockId,
                        );
                        return sibling?.text ?? null;
                      },
                      meta: {
                        nodeId: docNodeState.id,
                        bookId: docNodeState.bookId,
                        language: docNodeState.language,
                      },
                    },
                  );
                  if (!result) {
                    toast.error('Selection must stay inside a single block');
                    return;
                  }
                  openEditLink(result.sourceRef, result.mode);
                });
              },
            },
          },
          color: { class: ColorTool as never },
          fontSize: { class: FontSizeTool as never },
        },
        data: { time: Date.now(), version: '2.30', blocks: initBlocks },
        onReady: () => {
          isReadyRef.current = true;
          // EditorJS finishes rendering AFTER React commit, so the initial
          // useEffect can't see .ce-block elements yet. Sync prefixes now.
          syncBlockNames();
          // Let the real content's layout settle for one frame before
          // dropping the reserved-height spacer (see `reservedHeight`) —
          // avoids a visible collapse-then-grow between the spacer height
          // and the actual rendered height.
          requestAnimationFrame(() => {
            if (!cancelled) setReservedHeight(null);
          });
        },
        onChange: (_api, event) => {
          if (isProgrammaticRenderRef.current) return;
          // Structural mutations (add/remove/move) must sync immediately so
          // block-menu actions never see a short EditorJS id. Text edits stay
          // debounced to avoid a dispatch on every keystroke.
          if (isStructuralEditorChange(event)) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = null;
            void runSaveRef.current().then(() => {
              setEditorFlushDirty(parentIdRef.current, false);
            });
            return;
          }
          setEditorFlushDirty(parentIdRef.current, true);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            void requestSaveRef.current().then(() => {
              setEditorFlushDirty(parentIdRef.current, false);
            });
          }, DEBOUNCE_MS);
        },
      });

      editorRef.current = editor;
    }, 0);

    return () => {
      const wasReady = isReadyRef.current;
      cancelled = true;
      isDestroyedRef.current = true;
      isReadyRef.current = false;
      isProgrammaticRenderRef.current = false;
      clearTimeout(initTimer);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      editorRef.current = null;
      if (keydownHolder) {
        keydownHolder.removeEventListener('keydown', handleKeyDown);
        keydownHolder.removeEventListener(
          'keydown',
          handleBackspaceCapture,
          true,
        );
        keydownHolder.removeEventListener(
          'click',
          handleRedactorClickCapture,
          true,
        );
      }

      if (editor && typeof editor.destroy === 'function') {
        try {
          editor.destroy();
        } catch {
          // EditorJS sometimes throws on destroy if holder is already gone
        }
      }
      if (holderRef.current) {
        holderRef.current.replaceChildren();
      }
      // If this instance was constructed but torn down before `onReady`
      // ever fired, nothing else will clear `reservedHeight` — drop it here
      // instead of leaving a stale spacer. A never-constructed instance
      // (still in the deferred `setTimeout`, or cancelled before it fired)
      // deliberately leaves any existing reservation alone — clearing it
      // here would reopen the exact collapse this state exists to prevent.
      if (editor && !wasReady) {
        setReservedHeight(null);
      }
    };
  }, [mountEpoch]);

  // Reactivation: once `shouldMountEditor` flips back to true while we're
  // showing the static equivalent, drop the snapshot and bump `mountEpoch`
  // so the creation effect above re-runs. It reads `siblingsRef.current`,
  // which is synced unconditionally on every `siblings` prop change (not
  // gated on whether an editor is mounted) and advanced by `runSave` after
  // our own edits — so reactivation always initializes from the latest
  // text, whether or not it was edited while mounted.
  useEffect(() => {
    if (shouldMountEditor) {
      if (staticSnapshot !== null) {
        // Measure the CURRENTLY on-screen static content — not the height
        // captured back when this cluster deactivated — since the text or
        // viewport width may have changed while it was offscreen. Held
        // until EditorJS reports `onReady` so the swap never collapses this
        // block's height, which is what otherwise shifts the scroll
        // position onto the next sibling.
        const measured = wrapperRef.current?.getBoundingClientRect().height;
        if (measured) setReservedHeight(measured);
        setStaticSnapshot(null);
        setMountEpoch(v => v + 1);
      }
      return;
    }

    // Going offscreen and unpinned: deactivate, but only if an editor is
    // actually mounted right now (nothing to do if it was never
    // constructed, or is already showing the static equivalent).
    if (staticSnapshot !== null) return;
    if (deactivationInFlightRef.current) return;
    if (!editorRef.current) return;

    deactivationInFlightRef.current = true;
    void (async () => {
      try {
        // 1. Pins (focus/menu/selection/modal) are already reflected in
        //    `shouldMountEditor` itself — reaching this branch means none
        //    are held. 2. Await this owner's pending/in-flight save so a
        //    debounced edit from just before scrolling away never gets
        //    dropped.
        await flushPendingSaveRef.current();

        // Re-check after the async flush: a pin may have been taken, or
        // the block may have scrolled back into view, while we were
        // awaiting the save.
        if (shouldMountEditorRef.current) return;

        // 3. Measure the rendered height before destroying so the static
        // equivalent can hold that height and avoid a layout jump.
        const measuredHeight =
          holderRef.current?.getBoundingClientRect().height ?? 0;

        // 4. Destroy EditorJS.
        const editor = editorRef.current;
        editorRef.current = null;
        isDestroyedRef.current = true;
        isReadyRef.current = false;
        if (editor && typeof editor.destroy === 'function') {
          try {
            editor.destroy();
          } catch {
            // EditorJS sometimes throws on destroy if the holder is already gone.
          }
        }
        setOpenMenuId(null);
        setBlockElements([]);

        // 5. Render the static equivalent at the retained height.
        setStaticSnapshot({ minHeight: measuredHeight });
      } finally {
        deactivationInFlightRef.current = false;
      }
    })();
  }, [shouldMountEditor, staticSnapshot]);

  useEffect(() => {
    const root = holderRef.current;
    if (!root) return;

    const blocks = root.querySelectorAll<HTMLElement>('.ce-block');
    blocks.forEach(blockEl => {
      const editorBlockId = blockEl.getAttribute('data-id') ?? '';
      const docId = idMappingRef.current.get(editorBlockId) ?? editorBlockId;
      if (docId === openMenuId) {
        blockEl.classList.add('is-menu-open');
      } else {
        blockEl.classList.remove('is-menu-open');
      }
    });
  }, [openMenuId, blockElements]);

  return (
    <div
      ref={wrapperRef}
      className={`doc-editor-paragraph-cluster is-${firstStyle} ${openMenuId ? 'is-menu-open' : ''}`}
      style={styleVars}
    >
      {staticSnapshot ? (
        <div
          className="doc-editor-paragraph-cluster__static"
          style={
            staticSnapshot.minHeight > 0
              ? { minHeight: staticSnapshot.minHeight }
              : undefined
          }
        >
          {siblings.map(sibling => (
            <div
              key={sibling.id}
              data-doc-id={sibling.id}
              className="doc-editor-paragraph-cluster__static-block"
            >
              {namesMap.get(sibling.id) && (
                <span className="doc-editor-paragraph-cluster__static-name">
                  {namesMap.get(sibling.id)}
                </span>
              )}
              <span
                className="doc-editor-paragraph-cluster__static-content"
                dangerouslySetInnerHTML={{
                  __html: sanitizeBlockHtml(
                    renderTextToHtml(sibling.text, refMap, sibling.id),
                  ),
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div
          ref={holderRef}
          className="doc-editor-paragraph-cluster__editor"
          style={
            reservedHeight != null ? { minHeight: reservedHeight } : undefined
          }
        />
      )}

      {blockElements.map(({ id, editorBlockId, el }) => {
        const state = store.getState();
        const pos = getSiblingPosition(state, id);
        const sibling = siblings.find(s => s.id === id);
        // Flush any in-flight/pending save first so a block created by a
        // fast paste (or any other race) is guaranteed a real doc UUID in
        // idMappingRef before we resolve and dispatch its id — otherwise a
        // still-short EditorJS id could reach the backend and fail
        // "Validation failed (uuid is expected)".
        const resolveDocId = async (): Promise<string | null> => {
          // `BlockMenu.handleDelete` (and the add-above/add-below handlers)
          // close the menu — unpinning `'menu'` — before this async work
          // even starts. Without our own pin, a cluster that scrolls just
          // offscreen mid-flush could begin tearing down its EditorJS
          // instance while a block-menu action is still resolving.
          pin('pending-action');
          try {
            await flushPendingSave();
            const docId = resolveSyncedDocId(
              editorBlockId,
              idMappingRef.current,
              store.getState().documentData.byId,
            );
            if (!docId) {
              toast.error('Block is still syncing. Please try again.');
            }
            return docId;
          } finally {
            unpin('pending-action');
          }
        };
        return createPortal(
          <div className="ce-block__block-menu">
            <BlockMenu
              variant="leaf"
              showNumbering={false}
              onDelete={() => {
                void (async () => {
                  const docId = await resolveDocId();
                  if (!docId) return;
                  const currentSibling = siblingsRef.current.find(
                    s => s.id === docId,
                  );
                  dispatch(
                    setDeleteBlockConfirmationModal({
                      open: true,
                      data: {
                        bookId: bookId ?? '',
                        nodeId: nodeId ?? '',
                        blockId: docId,
                        blockType:
                          currentSibling?.style ?? sibling?.style ?? 'text',
                      },
                    }),
                  );
                })();
              }}
              onAddAbove={() => {
                void (async () => {
                  const docId = await resolveDocId();
                  if (!docId) return;
                  dispatch(
                    setAddBlockIntent({
                      mode: 'sibling-before',
                      targetId: docId,
                    }),
                  );
                  dispatch(setNewBlockModal(true));
                })();
              }}
              onAddBelow={() => {
                void (async () => {
                  const docId = await resolveDocId();
                  if (!docId) return;
                  dispatch(
                    setAddBlockIntent({
                      mode: 'sibling-after',
                      targetId: docId,
                    }),
                  );
                  dispatch(setNewBlockModal(true));
                })();
              }}
              onMoveUp={() => handleGlobalMove(editorBlockId, 'up')}
              onMoveDown={() => handleGlobalMove(editorBlockId, 'down')}
              canMoveUp={pos.canMoveUp}
              canMoveDown={pos.canMoveDown}
              onOpenChange={open => setOpenMenuId(open ? id : null)}
            />
            <BlockReferencedIndicator blockId={id} variant="compact" />
          </div>,
          el,
        );
      })}
    </div>
  );
}

export { DocumentEditorParagraphCluster };
